import { CompletedCourseRequestRecord, ICourse, IRecommendedCourse } from "@features/shared/entities/courses";
import {
    CompletedLessonRequestRecord,
    FeedbackRequestRecord,
    ILesson,
    LessonCompleteResponse,
    ReportIssueRequestRecord
} from "@features/shared/entities/lessons";
import { CompletedTopicRequestRecord, ITopic } from "@features/shared/entities/topics";
import {
    ICheckTaskRequestRecord,
    ISkipTaskRequestRecord,
    ITask,
    ICheckTaskV2RequestRecord
} from "@features/shared/entities/tasks";
import { HttpRequest, IHttpClient, request as RequestClass } from "@features/shared/request/model";
import { IStudentCourse, IStudentTopic, IStudentLesson, IStudentTask } from "../entities/enrollments";
import { IStudent, IUpsellData, IUpsellsConfigResponse, IUpsellsPricesResponse } from "../entities/student";
import { IAiTipItem, IAiTipCategory, IAiTip } from "../entities/tips";
import { CountryResponse, IPaymentPlan, SolidFormResponse, EcommpayPaymentObject } from "../entities/payment";
import { ISolidgatePlan, ITruegatePlan } from "../entities/payment";
import { FeatureCounterResponse, AnalyticsEventPayload, DeviceDataPayload } from "@features/shared/entities/features-api";
import { IMetadataResponse, ISetMetadataRequest, IDeleteMetadataRequest } from "../entities/metadata";
import { getCustomError, ICustomError } from "../entities/notification";
import { MetaPredictionParams, MetaPredictionResponse } from "../entities/meta-prediction";
import { withContentParams, withLocale } from "@features/shared/locale";

const SERVER_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

export class TCourseDataApi {
    constructor(private httpClient: IHttpClient) { }

    async getCourses() {
        let includeAll = false;
        if (typeof window !== "undefined") {
            try {
                includeAll = Boolean(window.localStorage.getItem("all_courses"));
            } catch (_) { }
        }

        return await this.httpClient.do<ICourse[]>(withLocale(`${SERVER_URL}/courses${includeAll ? "?all_courses=true" : ""}`)); // +
    }

    async getCoursesRecommendations(courseId: number) {
        return await this.httpClient.do<IRecommendedCourse[]>(withLocale(`${SERVER_URL}/courses/${courseId}/recommendations`));
    }

    async getAllCoursesIncludingDrafts() {
        return await this.httpClient.do<ICourse[]>(withLocale(`${SERVER_URL}/courses?all_courses=true`));
    }

    async completeCourse(data: CompletedCourseRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/courses/complete`, "POST", data); //+
    }

    async getTopicsByCourseId(courseId: number) {
        return await this.httpClient.do<ITopic[]>(withLocale(`${SERVER_URL}/topics/course/${courseId}`)); // +
    }

    async completeTopic(data: CompletedTopicRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/topics/complete`, "POST", data); // +
    }

    async getLessonsByTopicId(topicId: number) {
        return await this.httpClient.do<ILesson[]>(withLocale(`${SERVER_URL}/lessons/topic/${topicId}`)); // +
    }

    async completeLesson(data: CompletedLessonRequestRecord) {
        return await this.httpClient.do<LessonCompleteResponse>(`${SERVER_URL}/lessons/complete`, "POST", data); // +
    }

    async getTasksByLessonId(lessonId: number) {
        return await this.httpClient.do<ITask[]>(`${SERVER_URL}/tasks/lesson/${lessonId}`); // +
    }

    async checkTask(data: ICheckTaskRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/answer/check`, "POST", data); // +
    }

    // V2: support string taskId and explicit status
    async checkTaskV2(data: ICheckTaskV2RequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/answer/check`, "POST", data);
    }

    async skipTask(data: ISkipTaskRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/skip`, "POST", data); // +
    }

    async skipTaskList(data: { lessonId: number; tasksIds: string[] }) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/skip-list`, "POST", data);
    }

    async resetTaskList(data: { lessonId: number; tasksIds: string[] }) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/reset-list`, "POST", data);
    }

    async resetTask(data: ISkipTaskRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/tasks/reset`, "POST", data); // +
    }

    async sendFeedback(data: FeedbackRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/feedback/content`, "POST", data); // +
    }

    async sendRepportIssue(data: ReportIssueRequestRecord) {
        return await this.httpClient.do(`${SERVER_URL}/feedback/issues`, "POST", data); // +
    }

    async trackAnalyticsEvent(data: AnalyticsEventPayload) {
        return await this.httpClient.do(`${SERVER_URL}/analytics/events/track`, "POST", data);
    }

    async trackDeviceData(data: DeviceDataPayload) {
        const visitUrl = typeof window !== "undefined" ? window.location.href : undefined;
        const payload: DeviceDataPayload = {
            ...data,
            visit_url: visitUrl
        };

        return await this.httpClient.do(`${SERVER_URL}/analytics/device-data/track`, "POST", payload);
    }

    async getMetaPrediction(params: MetaPredictionParams) {
        const searchParams = new URLSearchParams({
            email: params.email,
            plan_code: params.plan_code
        });

        if (params.onboarding_id) {
            searchParams.set("onboarding_id", params.onboarding_id);
        }
        if (params.order_id) {
            searchParams.set("order_id", params.order_id);
        }

        return await this.httpClient.do<MetaPredictionResponse>(
            `${SERVER_URL}/analytics/meta-prediction?${searchParams.toString()}`
        );
    }
}

export const CourseDataApi = new TCourseDataApi(HttpRequest);

export interface Credentials {
    email: string;
    password: string;
    code?: string;
}

export interface SignupCredentials {
    email: string;
    deviceId: string;
    qs: string;
    gaClientId: string;
    fbPixelId: string;
    fbClickId: string;
}

export type LoginEmailNextAction = "enter_password" | "verify_email" | "error" | "redirect";

export interface ILoginEmailStatusResponse {
    status: "completed" | "pending" | "not_found" | "other_project";
    hasPassword: boolean;
    requiresVerification: boolean;
    verificationMethod: "email_code" | "none";
    nextAction: LoginEmailNextAction;
    // nextAction "redirect": the account belongs to another project, whose landing this is.
    redirectUrl?: string;
}

export interface IDeviceIdsResponse {
    email: string;
    deviceIds: string[];
}

export class TStudentApi {
    constructor(private httpClient: IHttpClient) { }

    async signInByEmail(data: Credentials) {
        return await this.httpClient.do<string>(`${SERVER_URL}/auth/sign-in`, "POST", data); // +
    }

    async verifyMagicLink(token: string) {
        const res = await this.httpClient.do<{ token: string } | string>(`${SERVER_URL}/auth/magic-link/verify`, "POST", { token });
        return typeof res === "string" ? res : res.token;
    }

    async getOneTimeToken() {
        return await this.httpClient.do<{ token: string }>(`${SERVER_URL}/auth/magic-link/token`)
    }

    async singUpByEmail(data: SignupCredentials) {
        return await this.httpClient.do<{ userId: number; userPublicId: string }>(
            `${SERVER_URL}/auth/sign-up`,
            "POST",
            data
        ); // +
    }
    async singUpByEmailApp(data: { email: string }) {
        return await this.httpClient.do(`${SERVER_URL}/auth/sign-up-app`, "POST", data); // +
    }

    async loginEmailStatus(email: string) {
        return await this.httpClient.do<ILoginEmailStatusResponse>(`${SERVER_URL}/auth/login/email-status`, "POST", {
            email
        });
    }

    async getDeviceIdsByEmail(email: string) {
        return await this.httpClient.do<IDeviceIdsResponse>(
            `${SERVER_URL}/auth/device-ids?email=${encodeURIComponent(email)}`
        );
    }

    async getUserIdByEmail(email: string) {
        return await this.httpClient.do<{ userId: number }>(
            `${SERVER_URL}/auth/user-id?email=${encodeURIComponent(email)}`
        );
    }

    async readProfile() {
        return await this.httpClient.do<IStudent>(`${SERVER_URL}/profile`); // +
    }

    async deleteProfile() {
        return await this.httpClient.do<IStudent>(`${SERVER_URL}/profile`, "DELETE"); // +
    }

    async updateProfile(data: { firstName: string; lastName?: string }) {
        return await this.httpClient.do(`${SERVER_URL}/profile`, "PATCH", data);
    }

    async sendCode(email: string, isReset: boolean) {
        return await this.httpClient.do(`${SERVER_URL}/auth/code/send`, "POST", { email, is_reset: isReset }); // +
    }

    async confirmEmailAfterPurchase(emailFromPurchase: string, newEmail?: string) {
        return await this.httpClient.do<{
            message: string;
            status: boolean;
            email: string;
            email_changed: boolean;
        }>(`${SERVER_URL}/auth/confirm-email-after-purchase`, "POST", {
            email_from_purchase: emailFromPurchase,
            new_email: newEmail
        });
    }

    async setPasswordWithoutCode(email: string, password: string) {
        return await this.httpClient.do(`${SERVER_URL}/auth/set-password/without-code`, "POST", {
            email,
            password
        });
    }

    async changeEmail({ email, funnelFoxId, deviceId }: { email: string; funnelFoxId?: string; deviceId?: string }) {
        return await this.httpClient.do(`${SERVER_URL}/auth/change-email`, "POST", {
            email,
            deviceId,
            funnel_fox_id: funnelFoxId
        }); // +
    }

    async checkCode(email: string, code: string) {
        return await this.httpClient.do(`${SERVER_URL}/auth/code/check`, "POST", { email, code }); // +
    }

    async saveOnboardingAnswers(data: { answers: any; email: string; onboardingName: string }) {
        return await this.httpClient.do(`${SERVER_URL}/students/onboarding-answers`, "POST", data);
    }

    async getOnboardingAnswersByEmail(email: string) {
        return await this.httpClient.do<{ answers: any[] }>(
            `${SERVER_URL}/students/onboarding-answers?email=${encodeURIComponent(email)}`
        );
    }

    async getOnboardingData() {
        return await this.httpClient.do<{
            courseId: string;
            introLessons: [
                {
                    courseId: number;
                    topicId: number;
                    lessonId: number;
                }
            ];
        }>(`${SERVER_URL}/students/onboarding-course`);
    }

    async getUpsellData(variant?: string | null) {
        const query = variant ? `?variant=${encodeURIComponent(variant)}` : "";
        return await this.httpClient.do<IUpsellData[]>(`${SERVER_URL}/students/upsells-sequence${query}`);
    }

    // Unified upsell configuration (phase 1). `flags` are the current Amplitude
    // Experiment assignments, serialized as comma-separated `key:value` pairs.
    async getUpsellsConfig(flags: Record<string, string>) {
        const pairs = Object.entries(flags).map(([key, value]) => `${key}:${value}`);
        const query = pairs.length > 0 ? `?flags=${encodeURIComponent(pairs.join(","))}` : "";
        // Appends ?locale= (omitted for en) — the backend patches view_data
        // with the translation for that locale.
        return await this.httpClient.do<IUpsellsConfigResponse>(
            withContentParams(`${SERVER_URL}/students/upsells-config${query}`)
        );
    }

    // Display prices for every upsell in the student's context, independent of
    // the funnel chain: see IUpsellsPricesResponse. Appends ?locale= (omitted
    // for en) — the item names come from plan metadata in that language.
    async getUpsellsPrices() {
        return await this.httpClient.do<IUpsellsPricesResponse>(
            withContentParams(`${SERVER_URL}/students/upsells-prices`)
        );
    }

    async getUpsellDataAnon(email: string) {
        const encodedEmail = encodeURIComponent(email);
        return await this.httpClient.do<IUpsellData[]>(
            `${SERVER_URL}/students/upsells-sequence-anon?email=${encodedEmail}`
        );
    }

    // Metadata API methods
    async setMetadata(data: ISetMetadataRequest) {
        return await this.httpClient.do<IMetadataResponse>(`${SERVER_URL}/profile/metadata`, "POST", data);
    }

    async getMetadata() {
        return await this.httpClient.do<IMetadataResponse>(`${SERVER_URL}/profile/metadata`);
    }

    async deleteMetadata(data: IDeleteMetadataRequest) {
        return await this.httpClient.do<IMetadataResponse>(`${SERVER_URL}/profile/metadata`, "DELETE", data);
    }

    async setMetadataAnon(email: string, data: ISetMetadataRequest) {
        return await this.httpClient.do<IMetadataResponse>(`${SERVER_URL}/profile/metadata-anon`, "POST", {
            ...data,
            email
        });
    }

    static withBaseUrl(token: string, baseUrl: string) {
        const tokenStorage = {
            async getToken() {
                return token;
            },
            async setToken() { },
            async removeToken() { }
        };
        return new TStudentApi(new RequestClass(tokenStorage, baseUrl));
    }
}

export const StudentApi = new TStudentApi(HttpRequest);

export class TStudentProgressApi {
    constructor(private httpClient: IHttpClient) { }

    async getCoursesEnrollments() {
        return await this.httpClient.do<IStudentCourse[]>(`${SERVER_URL}/enrollments/courses`); // +
    }

    async getTopicsEnrollments() {
        return await this.httpClient.do<IStudentTopic[]>(`${SERVER_URL}/enrollments/topics`); // +
    }

    async getLessonsEnrollments() {
        return await this.httpClient.do<IStudentLesson[]>(`${SERVER_URL}/enrollments/lessons`); // +
    }

    async getTasksEnrollemnents() {
        return await this.httpClient.do<IStudentTask[]>(`${SERVER_URL}/enrollments/tasks`); // +
    }

    async enrollCourse(courseId: number) {
        return await this.httpClient.do(`${SERVER_URL}/enrollments/course`, "POST", { courseId }); // +
    }

    async enrollTopic(data: { courseId: number; topicId: number }) {
        return await this.httpClient.do(`${SERVER_URL}/enrollments/topic`, "POST", data); // +
    }

    async enrollLesson(data: { topicId: number; lessonId: number }) {
        return await this.httpClient.do(`${SERVER_URL}/enrollments/lesson`, "POST", data); // +
    }

    async enrollTask(data: { lessonId: number; taskId: string }) {
        return await this.httpClient.do(`${SERVER_URL}/enrollments/task`, "POST", data); // +
    }
}

export const StudentProgressApi = new TStudentProgressApi(HttpRequest);

export class TStudentTipsApi {
    constructor(private httpClient: IHttpClient) { }

    async getTipsCategories() {
        return await this.httpClient.do<IAiTipCategory[]>(withLocale(`${SERVER_URL}/tips/category/list`));
    }

    async getTipsByCategoryId(categoryId: number) {
        return await this.httpClient.do<IAiTipItem[]>(withLocale(`${SERVER_URL}/tips/category/${categoryId}`));
    }

    async getFeaturedTips() {
        return await this.httpClient.do<IAiTipItem[]>(withLocale(`${SERVER_URL}/tips/featured`));
    }

    async getTipById(tipId: number) {
        return await this.httpClient.do<IAiTip>(withLocale(`${SERVER_URL}/tips/${tipId}`));
    }

    async getAuthorById(authorId: number) {
        return await this.httpClient.do<IAiTipItem[]>(`${SERVER_URL}/author/${authorId}`);
    }
}

export const StudentTipsApi = new TStudentTipsApi(HttpRequest);

export interface IPaymentTransaction {
    id: number;
    productName: string;
    paymentStatus: string;
    amount: number;
    currency: string;
    transactionDate: string;
    paymentMethod: string;
    orderId: string;
}

export interface IAppleRestorePurchase {
    transactionId: string;
    appAccountToken: string;
}

export interface IPaymentHistoryResponse {
    count: number;
    success: boolean;
    transactions: IPaymentTransaction[];
}

export class TPaymentApi {
    constructor(private httpClient: IHttpClient) { }

    async getPaymentHistory() {
        return await this.httpClient.do<IPaymentHistoryResponse>(
            withContentParams(`${SERVER_URL}/payments/history`)
        );
    }

    async getEcommpayPlans(deviceId: string) {
        return await this.httpClient.do<IPaymentPlan[]>(`${SERVER_URL}/payments/ecomm/plans?dev_id=${deviceId}`);
    }

    async getEcommpayAnonPlans(deviceId: string) {
        return await this.httpClient.do<IPaymentPlan[]>(`${SERVER_URL}/payments/ecomm/plans-anon?dev_id=${deviceId}`);
    }

    async getEcommpayPlansAuth() {
        return await this.httpClient.do<IPaymentPlan[]>(`${SERVER_URL}/payments/ecomm/plans-auth`);
    }

    // `locale` (content locale, via withContentParams) selects the admin-authored
    // plan names (plan_metadata); `preview=1` is ignored by the plan endpoints.
    async getSolidgatePlans(ids?: string | string[]) {
        const idsParam = Array.isArray(ids) ? ids.filter(Boolean).join(",") : (ids ?? "").trim();
        const query = idsParam ? `?ids=${encodeURIComponent(idsParam)}` : "";
        return await this.httpClient.do<ISolidgatePlan[]>(
            withContentParams(`${SERVER_URL}/payments/solidgate/plans${query}`)
        );
    }

    async getApplePlans() {
        return await this.httpClient.do<IPaymentPlan[]>(`${SERVER_URL}/payments/apple/plans`);
    }

    async getSolidgateForm(
        userUuid: string,
        planId: string,
        currency: string,
        metadata?: {
            website?: string;
            trafficSource?: string;
            deviceId?: string;
            utm_campaign?: string;
            utm_content?: string;
            utm_id?: string;
            utm_medium?: string;
            utm_source?: string;
            utm_term?: string;
            onboarding_id?: string;
            consent_ad_user_data?: string;
            consent_ad_personalization?: string;
        }
    ) {
        const params = new URLSearchParams({
            dev_id: userUuid,
            product_id: planId,
            currency: currency
        });

        if (metadata) {
            const keyMap: Record<string, string> = {
                trafficSource: "traffic_source",
                deviceId: "device_id"
            };

            Object.entries(metadata).forEach(([key, value]) => {
                if (value) {
                    params.set(keyMap[key] ?? key, value);
                }
            });
        }

        return await this.httpClient.do<SolidFormResponse>(
            `${SERVER_URL}/payments/solidgate/form/device?${params.toString()}`
        );
    }

    async getSolidgateFormByUserId(
        userUuid: string,
        planId: string,
        currency: string,
        metadata?: {
            website?: string;
            trafficSource?: string;
            deviceId?: string;
            onboardingId?: string | null;
            utm_campaign?: string;
            utm_content?: string;
            utm_id?: string;
            utm_medium?: string;
            utm_source?: string;
            utm_term?: string;
            consent_ad_user_data?: string;
            consent_ad_personalization?: string;
        }
    ) {
        const params = new URLSearchParams({
            user_id: userUuid,
            product_id: planId,
            currency: currency
        });

        if (metadata) {
            const keyMap: Record<string, string> = {
                trafficSource: "traffic_source",
                deviceId: "device_id"
            };

            Object.entries(metadata).forEach(([key, value]) => {
                if (value) {
                    params.set(keyMap[key] ?? key, value);
                }
            });
        }

        return await this.httpClient.do<SolidFormResponse>(
            `${SERVER_URL}/payments/solidgate/form/user?${params.toString()}`
        );
    }

    // What the backend's logged webhooks say about an order a page started:
    // "unknown" until the first one arrives.
    async getSolidgateOrderStatus(orderId: string) {
        return await this.httpClient.do<{ status: "paid" | "pending" | "failed" | "unknown" }>(
            `${SERVER_URL}/payments/solidgate/order-status?order_id=${encodeURIComponent(orderId)}`
        );
    }

    async sendPaymentId(data: { deviceId: string; paymentId: string }) {
        return await this.httpClient.do(`${SERVER_URL}/payments/ecomm/paid`, "POST", data);
    }

    async getEcommpayPaymentObject(planId: number, deviceId: string, platform: "ios" | "android" | "desktop") {
        return await this.httpClient.do<EcommpayPaymentObject>(
            `${SERVER_URL}/payments/ecomm/payment_object?plan_id=${planId}&dev_id=${deviceId}&platform=${platform}`
        );
    }

    async getTruegatePlans() {
        return await this.httpClient.do<ITruegatePlan[]>(
            withContentParams(`${SERVER_URL}/payments/truegate/plans`)
        );
    }

    /**
     * The plan set an onboarding is configured with, in the admin's order
     * (Admin -> Onboarding plans; table `onboarding_plans`). Used by the renew
     * paywall so its plans are editable without a deploy; `locale` selects the
     * admin-authored plan names, like the catalog endpoints.
     */
    async getOnboardingPlans<T = ISolidgatePlan[]>(onboardingId: string, platform: "solidgate" | "truegate") {
        const params = new URLSearchParams({ onboarding_id: onboardingId, platform });
        return await this.httpClient.do<T>(
            withContentParams(`${SERVER_URL}/payments/plans/onboarding?${params.toString()}`)
        );
    }

    async appleRestorePurchase(transactionId: string, appAccountToken: string) {
        return await this.httpClient.do<string>(`${SERVER_URL}/payments/apple/restore-purchase`, "POST", {
            transactionId,
            appAccountToken
        });
    }
}

export const PaymentApi = new TPaymentApi(HttpRequest);

import { TTruegateApi } from "./truegate.api";
export const TruegateApi = new TTruegateApi(HttpRequest);

export class TUserApi {
    constructor(private httpClient: IHttpClient) { }

    // One lookup per page in the browser: several models ask for the visitor's
    // country on the same screen (subscriptions for $ vs US$, the payment model
    // for the fallback currency and the truegate payment country, upsells for
    // UPI), and on 3G each duplicate cost a round-trip in front of the plans.
    // The country does not change within a page; a failed lookup is not kept,
    // and on the server nothing is cached, so no visitor ever gets another's.
    private locationRequest: Promise<CountryResponse> | null = null;

    async getUserLocation() {
        if (typeof window === "undefined") {
            return await this.httpClient.do<CountryResponse>(`${SERVER_URL}/user/location`);
        }
        this.locationRequest ??= this.httpClient
            .do<CountryResponse>(`${SERVER_URL}/user/location`)
            .catch((error) => {
                this.locationRequest = null;
                throw error;
            });
        return await this.locationRequest;
    }
}

export const UserApi = new TUserApi(HttpRequest);

export class TFeatures {
    constructor(private httpClient: IHttpClient) { }

    async getFeatures(featureKey: string) {
        return await this.httpClient.do<FeatureCounterResponse>(
            `${SERVER_URL}/features/counter?featureKey=${featureKey}`
        );
    }

    async incrementFeature(featureKey: string, featureLimit?: number) {
        const query = featureLimit !== undefined ? `?featureLimit=${featureLimit}` : "";
        return await this.httpClient.do<FeatureCounterResponse>(
            `${SERVER_URL}/features/counter/increment${query}`,
            "POST",
            {
                featureKey: featureKey
            }
        );
    }
}

export const FeaturesApi = new TFeatures(HttpRequest);

export interface DailyTaskRespondRequest {
    task_id: string;
    user_input: string;
}

export interface DailyTaskValidateRequest {
    task_id: string;
    user_prompt: string;
}

export interface DailyTask extends ITask {
    is_completed: boolean;
}

export interface DailyTaskResponse {
    response: string;
}

export interface DailyTaskValidateResponseItem {
    completed: boolean;
    description: string;
}

export interface DailyTaskValidateResponse extends Array<DailyTaskValidateResponseItem> { }

export class TDailyTaskApi {
    constructor(private httpClient: IHttpClient) { }

    async getDailyTask() {
        try {
            return await this.httpClient.do<DailyTask>(`${SERVER_URL}/daily-task`);
        } catch (err) {
            const customErr = getCustomError(err);
            if (customErr && customErr.status === 404) {
                return null;
            } else {
                throw err;
            }
        }
    }

    async respondToDailyTask(data: DailyTaskRespondRequest) {
        return await this.httpClient.do<DailyTaskResponse>(`${SERVER_URL}/daily-task/respond`, "POST", data);
    }

    async validateDailyTask(data: DailyTaskValidateRequest) {
        return await this.httpClient.do<DailyTaskValidateResponse>(`${SERVER_URL}/daily-task/validate`, "POST", data);
    }
}

export const DailyTaskApi = new TDailyTaskApi(HttpRequest);
